import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ServerInferRequest } from "@ts-rest/core";
import { taskGroupContract } from "@common/contracts/task-group.contract";
import {
    TaskGroup,
    TaskGroupSchema,
    TaskGroupTaskCount,
} from "@common/types/taskGroup";
import { useScope } from "@state/scopeContext.lib";
import { taskGroupClient, taskGroupQueryKeys } from "./taskGroup.api";
import { invalidateAllTaskQueries } from "./taskQueryKeys";

export function useOrganizationTaskGroups() {
    const { organizationId } = useScope();

    return useQuery({
        queryKey: organizationId
            ? taskGroupQueryKeys.organizationList(organizationId)
            : [],
        queryFn: async () => {
            if (!organizationId) {
                return [];
            }

            const response =
                await taskGroupClient.listAvailableTaskGroupsForOrganization({
                    params: { organizationId },
                });

            return response.body.taskGroups.map((taskGroup) =>
                TaskGroupSchema.parse(taskGroup),
            );
        },
        enabled: !!organizationId,
    });
}

export function useOrganizationTaskGroupCounts() {
    const { organizationId } = useScope();

    return useQuery({
        queryKey: organizationId
            ? taskGroupQueryKeys.organizationCounts(organizationId)
            : [],
        queryFn: async () => {
            if (!organizationId) {
                return [] as TaskGroupTaskCount[];
            }

            const response =
                await taskGroupClient.countTasksByGroupForOrganization({
                    params: { organizationId },
                });

            return response.body.counts;
        },
        enabled: !!organizationId,
    });
}

export function useCreateOrganizationTaskGroupMutation() {
    const queryClient = useQueryClient();
    const { organizationId } = useScope();

    return useMutation({
        mutationFn: async (
            body: ServerInferRequest<
                typeof taskGroupContract.createOrganizationTaskGroup
            >["body"],
        ) => {
            if (!organizationId) {
                throw new Error("No active organization");
            }

            const response = await taskGroupClient.createOrganizationTaskGroup({
                params: { organizationId },
                body,
            });

            return TaskGroupSchema.parse(response.body);
        },
        onSuccess: async () => {
            if (!organizationId) {
                return;
            }

            await Promise.all([
                queryClient.invalidateQueries({
                    queryKey: taskGroupQueryKeys.organization(organizationId),
                    exact: false,
                }),
                invalidateAllTaskQueries(queryClient),
            ]);
        },
    });
}

export function useUpdateOrganizationTaskGroupMutation(taskGroup?: TaskGroup) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (
            body: ServerInferRequest<
                typeof taskGroupContract.updateOrganizationTaskGroup
            >["body"],
        ) => {
            if (!taskGroup?.organizationId) {
                throw new Error("No task group selected");
            }

            const response = await taskGroupClient.updateOrganizationTaskGroup({
                params: {
                    organizationId: taskGroup.organizationId,
                    groupId: taskGroup.id,
                },
                body,
            });

            return TaskGroupSchema.parse(response.body);
        },
        onSuccess: async () => {
            if (!taskGroup?.organizationId) {
                return;
            }

            await Promise.all([
                queryClient.invalidateQueries({
                    queryKey: taskGroupQueryKeys.organization(
                        taskGroup.organizationId,
                    ),
                    exact: false,
                }),
                invalidateAllTaskQueries(queryClient),
            ]);
        },
    });
}

export function useDeleteOrganizationTaskGroupMutation(taskGroup?: TaskGroup) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async () => {
            if (!taskGroup?.organizationId) {
                throw new Error("No task group selected");
            }

            await taskGroupClient.deleteOrganizationTaskGroup({
                params: {
                    organizationId: taskGroup.organizationId,
                    groupId: taskGroup.id,
                },
            });
        },
        onSuccess: async () => {
            if (!taskGroup?.organizationId) {
                return;
            }

            await Promise.all([
                queryClient.invalidateQueries({
                    queryKey: taskGroupQueryKeys.organization(
                        taskGroup.organizationId,
                    ),
                    exact: false,
                }),
                invalidateAllTaskQueries(queryClient),
            ]);
        },
    });
}
